/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useCallback, useState } from "react";
import { Image, ScrollView, View } from "react-native";
import { useLazyQuery } from "@apollo/client";
import parse from "html-react-parser";
import Icon from "react-icons-kit";
import { exclamationCircle } from "react-icons-kit/fa/exclamationCircle";
import { setContext } from "@apollo/client/link/context";
import md5 from "blueimp-md5";

import { LoadingView, Text, styles } from "UpduoLibrary";
import {
  skaleWhite,
  skaleWebBackgroundGray,
  skaleWebTextGray,
  skaleSummitDarkGray,
  colorWithOpacity,
} from "config/UpduoColors";
import { getNodes } from "config/Utilities";
import SkillModuleDetail from "./SkillModuleDetail";
import PhonePreview from "./PhonePreview";
import {
  GET_SKILL_DETAIL,
  GET_CURRICULUM_LIST,
  GET_PRIMARY_MEMBER_LIST,
  GET_PRIMARY_MEMBER_TOKEN,
} from "graphql/internal/Queries";

import * as UpduoAnalytics from "config/UpduoAnalytics";
import { GetCurriculumListQuery } from "graphql/internal/generated";
import { useEventListener } from "config/Hooks";
import { generateApolloClient } from "config/UpduoApolloClient";
import { asyncCallApi } from "config/UpduoService";
import UpduoLogo from "assets/upduo-black.svg";

export default function ContentLibraryRoute() {
  const [isLoading, setIsLoading] = useState(true);
  const [skillModulesList, setSkillModulesList] = useState<any[]>([]);

  const [skillId, setSkillId] = useState("");
  const [previewIndex, setPreviewIndex] = useState(0);
  const [teacherTitle, setTeacherTitle] = useState("");
  const [learnerTitle, setLearnerTitle] = useState("");
  const [skillName, setSkillName] = useState("");
  const [skillSessionSteps, setSkillSessionSteps] = useState<any[]>([]);
  const [skillDescription, setSkillDescription] = useState("");

  const [authToken, setAuthToken] = useState("");
  const [MemberApolloClient, setMemberApolloClient] = useState<any>();

  async function authenticateLibrary() {
    const client = `web_${md5(window.navigator.appVersion)}`;
    const response = await asyncCallApi("postLogin", {
      email: "library@upduo.com",
      password: "Testingtesting123",
      client,
    });
    const responseJson = await response?.json();
    const primaryAuthLink = setContext((_, { headers }) => {
      // get the authentication token from environment
      // return the headers to the context so httpLink can read them
      return {
        headers: {
          ...headers,
          authorization: `Token ${responseJson.key}`,
        },
      };
    });
    const PrimaryApolloClient = generateApolloClient(primaryAuthLink);
    const { data: getPrimaryMemberListData } = await PrimaryApolloClient.query({
      query: GET_PRIMARY_MEMBER_LIST,
      fetchPolicy: "network-only",
      variables: {
        primaryMemberUserList: true,
      },
    });
    if (getPrimaryMemberListData.users) {
      const edges = getPrimaryMemberListData.users.edges;
      if (edges.length > 0) {
        const response = edges.map(({ node }) => node);
        const { data: getPrimaryMemberTokenData } =
          await PrimaryApolloClient.query({
            query: GET_PRIMARY_MEMBER_TOKEN,
            fetchPolicy: "network-only",
            variables: {
              getPrimaryMemberTokenRequest: {
                userId: response[0].id,
              },
            },
          });
        setAuthToken(getPrimaryMemberTokenData.user.token);
      }
    }
  }

  useEffect(() => {
    authenticateLibrary();
  }, []);

  useEffect(() => {
    const authLink = setContext((_, { headers }) => {
      // return the headers to the context so httpLink can read them
      return {
        headers: {
          ...headers,
          authorization: `Token ${authToken}`,
        },
      };
    });
    const newMemberApolloClient = generateApolloClient(authLink);
    setMemberApolloClient(newMemberApolloClient);
  }, [authToken]);

  const [getCurriculumList, { loading: getCurriculumListLoading }] =
    useLazyQuery<GetCurriculumListQuery>(GET_CURRICULUM_LIST, {
      fetchPolicy: "network-only",
      client: MemberApolloClient,
      onError: (error) => {
        console.error("ERROR getCurriculumList", error);
        UpduoAnalytics.logEvent("app-curriculum-getcurriculumlist-error", {
          error,
        });
      },
      onCompleted: ({ staffSkillModules: getCurriculumListData }) => {
        const formattedCurriculumList = getNodes(getCurriculumListData).map(
          (skillModule) => {
            return {
              ...skillModule,
              activeSkillList: getNodes(skillModule.activeSkillList),
            };
          }
        );
        setSkillModulesList(formattedCurriculumList);
        setIsLoading(false);
      },
    });

  useEffect(() => {
    if (MemberApolloClient) {
      getCurriculumList();
    }
  }, [MemberApolloClient]);

  const roles = ["teacher", "learner"];

  const [getSkillDetail, {}] = useLazyQuery(GET_SKILL_DETAIL, {
    fetchPolicy: "network-only",
    client: MemberApolloClient,
    onCompleted: ({ skill }) => {
      setSkillId(skill.id);
      setTeacherTitle(skill.titleTeacher);
      setLearnerTitle(skill.titleLearner);
      setSkillName(skill.name);
      setSkillDescription(skill.description);
      // parse and extract detailTitle and detailContent from detail poperty (if non-empty) from sessionSteps[teacher] or sessionSteps[learner]
      const newSkillSessionSteps = JSON.parse(skill.sessionSteps);
      let newSkillStepDetail;
      for (let j = 0; j < newSkillSessionSteps.length; j++) {
        for (const role of roles) {
          newSkillStepDetail = _handleParseSessionStepDetail(
            newSkillSessionSteps[j][role].detail
          );
          newSkillSessionSteps[j][role] = Object.assign(
            {},
            newSkillSessionSteps[j][role],
            newSkillStepDetail
          );
        }
        newSkillSessionSteps[j].id =
          _handleGenerateUniqueSingleId(newSkillSessionSteps);
        newSkillSessionSteps[j].teacher.id =
          _handleGenerateUniqueSingleId(newSkillSessionSteps);
        newSkillSessionSteps[j].learner.id = _handleGenerateUniqueSingleId(
          newSkillSessionSteps,
          newSkillSessionSteps[j].teacher.id
        );
      }
      setSkillSessionSteps(newSkillSessionSteps);
      setIsLoading(false);
    },
  });

  const _handleParseSessionStepDetail = (sessionStepDetailString) => {
    if (!sessionStepDetailString) {
      return {};
    }
    const rawSplit = sessionStepDetailString.split("</h4></b>");
    const detailTitle = rawSplit[0].replace("<b><h4>", "");
    return { detailTitle, detailContent: rawSplit[1] };
  };

  const _handleGenerateUniqueSingleId = (
    usedSkillSessionSteps,
    additionalId = ""
  ) => {
    const result = Math.random().toString(36).substring(7);
    for (let i = 0; i < skillSessionSteps.length; i++) {
      if (
        skillSessionSteps[i].id === result ||
        skillSessionSteps[i].teacher.id === result ||
        skillSessionSteps[i].learner.id === result
      ) {
        return _handleGenerateUniqueSingleId(
          usedSkillSessionSteps,
          additionalId
        );
      }
    }
    if (additionalId && additionalId === result) {
      return _handleGenerateUniqueSingleId(usedSkillSessionSteps, additionalId);
    }
    return result;
  };

  const _handleGetSkillDetail = (skillHash) => {
    setPreviewIndex(0);
    getSkillDetail({
      variables: {
        getSkillDetailRequest: { skillId: skillHash },
      },
    });
  };

  const _handleKeyDown = useCallback(
    (e) => {
      // Allow arrow keys to work for preview
      if (e.keyCode === 37 && previewIndex > 0) {
        // left arrow on preview screen
        e.preventDefault();
        setPreviewIndex((previousState) => previousState - 1);
      } else if (
        e.keyCode === 39 &&
        previewIndex < skillSessionSteps.length - 1
      ) {
        // right arrow on preview screen
        e.preventDefault();
        setPreviewIndex((previousState) => previousState + 1);
      }
    },
    [previewIndex, skillSessionSteps]
  );

  useEventListener("keydown", _handleKeyDown);

  const userActionShield = (
    <View
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: window.innerHeight,
        backgroundColor: colorWithOpacity(skaleWhite, 0.7),
        zIndex: 15,
      }}
    >
      <LoadingView topSpacing={150} />
    </View>
  );
  return (
    <View style={{ flex: 1 }}>
      <View style={styles.subNavigationBar} />
      <View style={[styles.routeBodyContainer, { zIndex: 10 }]}>
        <View
          style={{
            zIndex: 5,
            justifyContent: "center",
          }}
        >
          <View
            style={{
              display: "flex",
              flex: 1,
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Image
              source={{ uri: UpduoLogo }}
              resizeMode={"contain"}
              style={{
                width: 140,
                height: 40,
              }}
            />
            <View
              style={{
                alignSelf: "flex-end",
                justifyContent: "center",
                marginBottom: 5,
              }}
            >
              <Text
                style={{
                  fontSize: 30,
                  fontWeight: "bold",
                  color: skaleWebTextGray,
                }}
              >
                {"Content Library"}
              </Text>
            </View>
          </View>
        </View>
        <View
          style={{
            backgroundColor: skaleWhite,
            borderWidth: 1,
            borderColor: skaleWebBackgroundGray,
            marginHorizontal: 15,
            marginVertical: 28,
            borderRadius: 12,
            minWidth: 700,
            padding: 20,
            flexDirection: "row",
          }}
        >
          <ScrollView style={{ flex: 1, height: "80vh" }}>
            {skillModulesList.map((item, itemIndex) => (
              <SkillModuleDetail
                key={itemIndex}
                skillList={item.activeSkillList}
                skillModule={item}
                currentSkillId={skillId}
                _handleGetSkillDetail={_handleGetSkillDetail}
              />
            ))}
          </ScrollView>
          <View style={{ flex: 1 }}>
            {skillId ? (
              <View style={{ marginLeft: 20 }}>
                <Text isHeading={true} style={{ fontSize: 16 }}>
                  {"Lesson Description:"}
                </Text>
                <ScrollView
                  style={{
                    width: window.innerWidth * 0.5 - 50,
                    height: "10vh",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 16,
                      height: 60,
                      flex: 1,
                    }}
                  >
                    {parse(skillDescription)}
                  </Text>
                </ScrollView>
                <>
                  <View style={{ flexDirection: "row", alignSelf: "center" }}>
                    <PhonePreview
                      title={"This is Alice's view"}
                      isTeacher={true}
                      cardIndex={previewIndex}
                      onChangeIndex={(newIndex) => {
                        setPreviewIndex(newIndex);
                      }}
                      skillSessionSteps={skillSessionSteps}
                      teacherRole={teacherTitle}
                      learnerRole={learnerTitle}
                      skillName={skillName}
                      communityName={"Example Inc."}
                    />
                    <View style={{ width: 75 }} />
                    <PhonePreview
                      title={"This is Bob's view"}
                      isTeacher={false}
                      cardIndex={previewIndex}
                      skillSessionSteps={skillSessionSteps}
                      teacherRole={teacherTitle}
                      learnerRole={learnerTitle}
                      skillName={skillName}
                      communityName={"Example Inc."}
                    />
                  </View>
                  <View
                    style={{
                      flexDirection: "row",
                      margin: 20,
                    }}
                  >
                    <Icon size={15} icon={exclamationCircle} />
                    <Text
                      style={{ paddingLeft: 10, color: skaleSummitDarkGray }}
                    >
                      {
                        "Use left / right arrow keys to navigate the session cards."
                      }
                    </Text>
                  </View>
                </>
              </View>
            ) : null}
          </View>
        </View>
        {isLoading || getCurriculumListLoading ? userActionShield : null}
      </View>
    </View>
  );
}
